import { z } from 'zod';

import { JsonValueSchema, NonEmptyStringSchema } from './common';
import { ManagedSemanticBehaviorSchema } from './managed-behavior';
import { StepHookSchema } from './step-hook';
import {
  FUNNEL_UI_EVENT_CATALOG,
  UITriggerSchema,
  type UITrigger,
} from './ui-events';

export const FunnelEventSemanticKindSchema = z.enum([
  'observation',
  'command_result',
  'component_result',
  'authoritative_result',
  'structural_transition',
]);
export type FunnelEventSemanticKind = z.infer<
  typeof FunnelEventSemanticKindSchema
>;

export const FunnelEventExecutionOwnerSchema = z.enum([
  'none',
  'storefront_dispatcher',
  'funnels_transition_resolver',
  'domain_authority',
]);
export type FunnelEventExecutionOwner = z.infer<
  typeof FunnelEventExecutionOwnerSchema
>;

export const STOREFRONT_SEMANTIC_ACTION_TYPES = [
  'capture_lead',
  'reveal_coupon',
  'run_spin',
  'accept_offer',
  'decline_offer',
  'dismiss_experience',
  'add_to_cart',
  'start_checkout',
  'select_variant',
  'apply_coupon',
  'remove_from_cart',
] as const;

export const StorefrontSemanticActionTypeSchema = z.enum(
  STOREFRONT_SEMANTIC_ACTION_TYPES,
);
export type StorefrontSemanticActionType = z.infer<
  typeof StorefrontSemanticActionTypeSchema
>;

const SemanticActionBaseSchema = z.object({
  id: z.string().optional(),
  label: z.string().optional(),
  hook: StepHookSchema.optional(),
  managedBehavior: ManagedSemanticBehaviorSchema.optional(),
});

const CaptureLeadActionSchema = SemanticActionBaseSchema.extend({
  type: z.literal('capture_lead'),
  config: z
    .object({
      formId: NonEmptyStringSchema.optional(),
      leadSource: NonEmptyStringSchema.optional(),
    })
    .passthrough()
    .default({}),
});

const RevealCouponActionSchema = SemanticActionBaseSchema.extend({
  type: z.literal('reveal_coupon'),
  config: z
    .object({
      couponCode: NonEmptyStringSchema.optional(),
      targetBlockId: NonEmptyStringSchema.optional(),
    })
    .passthrough()
    .default({}),
});

const RunSpinActionSchema = SemanticActionBaseSchema.extend({
  type: z.literal('run_spin'),
  config: z
    .object({
      wheelId: NonEmptyStringSchema.optional(),
    })
    .passthrough()
    .default({}),
});

const OfferActionConfigSchema = z
  .object({
    offerId: NonEmptyStringSchema.optional(),
    stepId: NonEmptyStringSchema.optional(),
  })
  .passthrough()
  .default({});

const AcceptOfferActionSchema = SemanticActionBaseSchema.extend({
  type: z.literal('accept_offer'),
  config: OfferActionConfigSchema,
});

const DeclineOfferActionSchema = SemanticActionBaseSchema.extend({
  type: z.literal('decline_offer'),
  config: OfferActionConfigSchema,
});

const DismissExperienceActionSchema = SemanticActionBaseSchema.extend({
  type: z.literal('dismiss_experience'),
  config: z
    .object({
      experienceId: NonEmptyStringSchema.optional(),
    })
    .passthrough()
    .default({}),
});

const AddToCartActionSchema = SemanticActionBaseSchema.extend({
  type: z.literal('add_to_cart'),
  config: z
    .object({
      productId: NonEmptyStringSchema.optional(),
      product_id: NonEmptyStringSchema.optional(),
      variantId: NonEmptyStringSchema.optional(),
      variant_id: NonEmptyStringSchema.optional(),
      sku: NonEmptyStringSchema.optional(),
      quantity: z.number().int().positive().optional(),
      qty: z.number().int().positive().optional(),
      subscription: JsonValueSchema.optional(),
    })
    .passthrough()
    .default({}),
});

const StartCheckoutActionSchema = SemanticActionBaseSchema.extend({
  type: z.literal('start_checkout'),
  config: z
    .object({
      cartId: NonEmptyStringSchema.optional(),
      mode: z.enum(['cart', 'buy_now']).optional(),
    })
    .passthrough()
    .default({}),
});

const SelectVariantActionSchema = SemanticActionBaseSchema.extend({
  type: z.literal('select_variant'),
  config: z
    .object({
      productId: NonEmptyStringSchema.optional(),
      variantId: NonEmptyStringSchema.optional(),
      targetBlockId: NonEmptyStringSchema.optional(),
    })
    .passthrough()
    .default({}),
});

const ApplyCouponActionSchema = SemanticActionBaseSchema.extend({
  type: z.literal('apply_coupon'),
  config: z
    .object({
      couponCode: NonEmptyStringSchema.optional(),
    })
    .passthrough()
    .default({}),
});

const RemoveFromCartActionSchema = SemanticActionBaseSchema.extend({
  type: z.literal('remove_from_cart'),
  config: z
    .object({
      lineId: NonEmptyStringSchema.optional(),
      productId: NonEmptyStringSchema.optional(),
      variantId: NonEmptyStringSchema.optional(),
    })
    .passthrough()
    .default({}),
});

export const StorefrontSemanticActionSchema = z.discriminatedUnion('type', [
  CaptureLeadActionSchema,
  RevealCouponActionSchema,
  RunSpinActionSchema,
  AcceptOfferActionSchema,
  DeclineOfferActionSchema,
  DismissExperienceActionSchema,
  AddToCartActionSchema,
  StartCheckoutActionSchema,
  SelectVariantActionSchema,
  ApplyCouponActionSchema,
  RemoveFromCartActionSchema,
]);
export type StorefrontSemanticAction = z.infer<
  typeof StorefrontSemanticActionSchema
>;

export const FunnelEventSemanticDefinitionSchema = z
  .object({
    eventName: z.enum(FUNNEL_UI_EVENT_CATALOG),
    kind: FunnelEventSemanticKindSchema.exclude(['structural_transition']),
    acceptedTriggers: z.array(UITriggerSchema).min(1),
    surfaces: z.tuple([z.literal('funnel')]),
    requiredParameters: z.array(NonEmptyStringSchema),
    optionalParameters: z.array(NonEmptyStringSchema),
    executionOwner: FunnelEventExecutionOwnerSchema,
    actionType: StorefrontSemanticActionTypeSchema.nullable(),
    successBoundary: NonEmptyStringSchema,
    successBoundaryOwner: NonEmptyStringSchema,
    resultBehavior: z.enum([
      'emit_observation',
      'emit_result',
      'authority_emits_result',
    ]),
    managedDefault: z.boolean(),
    relatedBusinessFact: z
      .string()
      .regex(/^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)+\.v[1-9][0-9]*$/)
      .optional(),
  })
  .strict();
export type FunnelEventSemanticDefinition = z.infer<
  typeof FunnelEventSemanticDefinitionSchema
>;

type FunnelBehavioralEventName = (typeof FUNNEL_UI_EVENT_CATALOG)[number];

function definition(
  value: FunnelEventSemanticDefinition,
): FunnelEventSemanticDefinition {
  return FunnelEventSemanticDefinitionSchema.parse(value);
}

const funnelOnly: ['funnel'] = ['funnel'];
const click: UITrigger[] = ['onClick'];

export const FUNNEL_EVENT_SEMANTICS = {
  cta_clicked: definition({
    eventName: 'cta_clicked',
    kind: 'observation',
    acceptedTriggers: click,
    surfaces: funnelOnly,
    requiredParameters: [],
    optionalParameters: ['destination', 'label'],
    executionOwner: 'none',
    actionType: null,
    successBoundary: 'The shopper activates the authored CTA control.',
    successBoundaryOwner: 'storefront-funnel',
    resultBehavior: 'emit_observation',
    managedDefault: true,
  }),
  lead_captured: definition({
    eventName: 'lead_captured',
    kind: 'command_result',
    acceptedTriggers: ['onSubmit'],
    surfaces: funnelOnly,
    requiredParameters: ['value'],
    optionalParameters: ['formId', 'leadSource'],
    executionOwner: 'storefront_dispatcher',
    actionType: 'capture_lead',
    successBoundary: 'The trusted lead endpoint returns an accepted lead_ref.',
    successBoundaryOwner: 'storefront-bff',
    resultBehavior: 'emit_result',
    managedDefault: true,
  }),
  coupon_revealed: definition({
    eventName: 'coupon_revealed',
    kind: 'component_result',
    acceptedTriggers: click,
    surfaces: funnelOnly,
    requiredParameters: [],
    optionalParameters: ['couponCode', 'targetBlockId'],
    executionOwner: 'storefront_dispatcher',
    actionType: 'reveal_coupon',
    successBoundary: 'The coupon state becomes visible in the component.',
    successBoundaryOwner: 'storefront-funnel',
    resultBehavior: 'emit_result',
    managedDefault: true,
  }),
  spin_completed: definition({
    eventName: 'spin_completed',
    kind: 'component_result',
    acceptedTriggers: ['onClick', 'onTimeout'],
    surfaces: funnelOnly,
    requiredParameters: [],
    optionalParameters: ['wheelId'],
    executionOwner: 'storefront_dispatcher',
    actionType: 'run_spin',
    successBoundary: 'The spin resolves to one winning segment.',
    successBoundaryOwner: 'storefront-funnel',
    resultBehavior: 'emit_result',
    managedDefault: true,
  }),
  offer_accepted: definition({
    eventName: 'offer_accepted',
    kind: 'command_result',
    acceptedTriggers: click,
    surfaces: funnelOnly,
    requiredParameters: [],
    optionalParameters: ['offerId', 'stepId'],
    executionOwner: 'storefront_dispatcher',
    actionType: 'accept_offer',
    successBoundary: 'The server-validated Funnel accept operation succeeds.',
    successBoundaryOwner: 'tss-svc-funnels',
    resultBehavior: 'emit_result',
    managedDefault: true,
  }),
  offer_declined: definition({
    eventName: 'offer_declined',
    kind: 'command_result',
    acceptedTriggers: click,
    surfaces: funnelOnly,
    requiredParameters: [],
    optionalParameters: ['offerId', 'stepId'],
    executionOwner: 'storefront_dispatcher',
    actionType: 'decline_offer',
    successBoundary: 'The server-validated Funnel decline operation succeeds.',
    successBoundaryOwner: 'tss-svc-funnels',
    resultBehavior: 'emit_result',
    managedDefault: true,
  }),
  experience_dismissed: definition({
    eventName: 'experience_dismissed',
    kind: 'component_result',
    acceptedTriggers: ['onClose', 'onClick'],
    surfaces: funnelOnly,
    requiredParameters: [],
    optionalParameters: ['experienceId'],
    executionOwner: 'storefront_dispatcher',
    actionType: 'dismiss_experience',
    successBoundary: 'The Storefront closes the active experience.',
    successBoundaryOwner: 'storefront-funnel',
    resultBehavior: 'emit_result',
    managedDefault: true,
  }),
  block_viewed: definition({
    eventName: 'block_viewed',
    kind: 'observation',
    acceptedTriggers: ['onView'],
    surfaces: funnelOnly,
    requiredParameters: [],
    optionalParameters: ['visibilityThreshold'],
    executionOwner: 'none',
    actionType: null,
    successBoundary: 'The configured visibility threshold is reached.',
    successBoundaryOwner: 'storefront-funnel',
    resultBehavior: 'emit_observation',
    managedDefault: false,
  }),
  form_submitted: definition({
    eventName: 'form_submitted',
    kind: 'observation',
    acceptedTriggers: ['onSubmit'],
    surfaces: funnelOnly,
    requiredParameters: [],
    optionalParameters: ['formId'],
    executionOwner: 'none',
    actionType: null,
    successBoundary: 'Client-side form validation succeeds.',
    successBoundaryOwner: 'storefront-funnel',
    resultBehavior: 'emit_observation',
    managedDefault: false,
  }),
  scroll_depth_reached: definition({
    eventName: 'scroll_depth_reached',
    kind: 'observation',
    acceptedTriggers: ['custom:scroll_depth'],
    surfaces: funnelOnly,
    requiredParameters: ['threshold'],
    optionalParameters: [],
    executionOwner: 'none',
    actionType: null,
    successBoundary: 'The configured scroll threshold is reached.',
    successBoundaryOwner: 'storefront-funnel',
    resultBehavior: 'emit_observation',
    managedDefault: false,
  }),
  exit_intent: definition({
    eventName: 'exit_intent',
    kind: 'observation',
    acceptedTriggers: ['custom:exit_intent'],
    surfaces: funnelOnly,
    requiredParameters: [],
    optionalParameters: [],
    executionOwner: 'none',
    actionType: null,
    successBoundary: 'The Storefront exit-intent detector fires.',
    successBoundaryOwner: 'storefront-funnel',
    resultBehavior: 'emit_observation',
    managedDefault: false,
  }),
  add_to_cart: definition({
    eventName: 'add_to_cart',
    kind: 'command_result',
    acceptedTriggers: click,
    surfaces: funnelOnly,
    requiredParameters: ['productId'],
    optionalParameters: ['variantId', 'quantity', 'sku'],
    executionOwner: 'storefront_dispatcher',
    actionType: 'add_to_cart',
    successBoundary: 'Carts persists the requested line.',
    successBoundaryOwner: 'tss-svc-carts',
    resultBehavior: 'emit_result',
    managedDefault: true,
    relatedBusinessFact: 'product.added_to_cart.v1',
  }),
  checkout_started: definition({
    eventName: 'checkout_started',
    kind: 'command_result',
    acceptedTriggers: click,
    surfaces: funnelOnly,
    requiredParameters: [],
    optionalParameters: ['cartId', 'mode'],
    executionOwner: 'storefront_dispatcher',
    actionType: 'start_checkout',
    successBoundary: 'The checkout initiation operation succeeds.',
    successBoundaryOwner: 'storefront-checkout',
    resultBehavior: 'emit_result',
    managedDefault: true,
    relatedBusinessFact: 'checkout.started.v1',
  }),
  order_completed: definition({
    eventName: 'order_completed',
    kind: 'authoritative_result',
    acceptedTriggers: ['onView'],
    surfaces: funnelOnly,
    requiredParameters: [],
    optionalParameters: ['orderId'],
    executionOwner: 'domain_authority',
    actionType: null,
    successBoundary: 'The authoritative order owner confirms the order.',
    successBoundaryOwner: 'orders-or-checkout-authority',
    resultBehavior: 'authority_emits_result',
    managedDefault: false,
    relatedBusinessFact: 'checkout.order_placed.v1',
  }),
  order_updated: definition({
    eventName: 'order_updated',
    kind: 'authoritative_result',
    acceptedTriggers: ['onChange'],
    surfaces: funnelOnly,
    requiredParameters: [],
    optionalParameters: ['orderId'],
    executionOwner: 'domain_authority',
    actionType: null,
    successBoundary: 'The authoritative order mutation commits.',
    successBoundaryOwner: 'tss-svc-orders',
    resultBehavior: 'authority_emits_result',
    managedDefault: false,
  }),
  variant_selected: definition({
    eventName: 'variant_selected',
    kind: 'component_result',
    acceptedTriggers: ['onChange', 'onClick'],
    surfaces: funnelOnly,
    requiredParameters: ['variantId'],
    optionalParameters: ['productId', 'targetBlockId'],
    executionOwner: 'storefront_dispatcher',
    actionType: 'select_variant',
    successBoundary: 'The component commits the selected variant state.',
    successBoundaryOwner: 'storefront-funnel',
    resultBehavior: 'emit_result',
    managedDefault: true,
  }),
  coupon_applied: definition({
    eventName: 'coupon_applied',
    kind: 'command_result',
    acceptedTriggers: ['onSubmit', 'onClick'],
    surfaces: funnelOnly,
    requiredParameters: ['couponCode'],
    optionalParameters: [],
    executionOwner: 'storefront_dispatcher',
    actionType: 'apply_coupon',
    successBoundary:
      'Checkout accepts the server-validated coupon into the active checkout state.',
    successBoundaryOwner:
      'storefront-checkout-after-checkout-middleware-validation',
    resultBehavior: 'emit_result',
    managedDefault: true,
  }),
  remove_from_cart: definition({
    eventName: 'remove_from_cart',
    kind: 'command_result',
    acceptedTriggers: click,
    surfaces: funnelOnly,
    requiredParameters: ['productId', 'variantId'],
    optionalParameters: ['lineId'],
    executionOwner: 'storefront_dispatcher',
    actionType: 'remove_from_cart',
    successBoundary: 'Carts persists the line removal.',
    successBoundaryOwner: 'tss-svc-carts',
    resultBehavior: 'emit_result',
    managedDefault: true,
  }),
} as const satisfies Record<
  FunnelBehavioralEventName,
  FunnelEventSemanticDefinition
>;

export const STRUCTURAL_EVENT_SEMANTICS = {
  page_view: {
    executionOwner: 'funnels_transition_resolver',
    successBoundary: 'The evaluated Funnel step mounts.',
  },
  accept: {
    executionOwner: 'funnels_transition_resolver',
    successBoundary: 'The server accepts the current step transition.',
  },
  decline: {
    executionOwner: 'funnels_transition_resolver',
    successBoundary: 'The server declines the current step transition.',
  },
  timeout: {
    executionOwner: 'funnels_transition_resolver',
    successBoundary: 'The configured step timer expires.',
  },
  cart_updated: {
    executionOwner: 'funnels_transition_resolver',
    successBoundary: 'The authoritative cart mutation is available.',
  },
  order_created: {
    executionOwner: 'funnels_transition_resolver',
    successBoundary: 'A confirmed order becomes available.',
  },
  order_updated: {
    executionOwner: 'funnels_transition_resolver',
    successBoundary: 'An authoritative order mutation becomes available.',
  },
} as const;

export function getFunnelEventSemantic(
  eventName: string,
): FunnelEventSemanticDefinition | undefined {
  return FUNNEL_EVENT_SEMANTICS[
    eventName as FunnelBehavioralEventName
  ];
}

export function isFunnelCommandSemantic(eventName: string): boolean {
  const semantic = getFunnelEventSemantic(eventName);
  return (
    semantic?.kind === 'command_result' ||
    semantic?.kind === 'component_result'
  );
}

export const SemanticActionConfigValueSchema = JsonValueSchema;
